import { useState } from 'react'
import type { Moment } from '@domain/events.ts'
import type { FamilyState, Member, Task } from '@domain/state.ts'
import { memberDayOf, routinesOf, taskDayOf, tasksOf } from '@domain/state.ts'
import { es } from '../strings/es.ts'
import { Meter } from './Meter.tsx'

const ORDER: readonly Moment[] = ['morning', 'afternoon', 'evening']

/** Routines still to do, in the order of the day. */
export function pendingRoutines(state: FamilyState, member: Member, day: string): readonly Task[] {
  const pending: Task[] = []
  for (const moment of ORDER) {
    for (const task of routinesOf(state, member.id, moment)) {
      if (taskDayOf(state, day as never, member.id, task.id).status !== 'validated') {
        pending.push(task)
      }
    }
  }
  return pending
}

/**
 * One thing on the screen, and nothing else.
 *
 * A day with twelve cards on it is twelve decisions before the first move. For a
 * child who stalls at the start, or who is overwhelmed by choice, this is often
 * the whole difference: there is no choosing left to do, only doing.
 *
 * The way out is always visible, because a screen you cannot leave is its own
 * kind of pressure.
 */
export function OneThing({
  member,
  state,
  day,
  chestBlocked,
  onOpenTask,
  onOpenChest,
  onSeeAll,
  onShop,
  onTimer,
  onJuegos,
  onBack,
}: {
  member: Member
  state: FamilyState
  day: string
  chestBlocked: string | null
  onOpenTask: (task: Task) => void
  onOpenChest: () => void
  onSeeAll: () => void
  onShop: () => void
  onTimer: () => void
  onJuegos: () => void
  onBack: () => void
}) {
  const [missionIndex, setMissionIndex] = useState(0)
  const today = memberDayOf(state, day as never, member.id)
  const pending = pendingRoutines(state, member, day)
  const routine = pending[0]

  const missions = tasksOf(state, member.id, 'mission').filter(
    (task) => taskDayOf(state, day as never, member.id, task.id).status === 'pending',
  )
  const mission = missions.length === 0 ? undefined : missions[missionIndex % missions.length]
  const current = routine ?? mission
  // The one after this one, so the day is never a surprise. Announcing what
  // comes next is worth as much as showing one thing at a time.
  const after = pending[1]
  const progress = current
    ? current.steps.filter((s) =>
        taskDayOf(state, day as never, member.id, current.id).steps.has(s.id),
      ).length
    : 0

  return (
    <div className="mx-auto flex min-h-dvh max-w-xl flex-col px-5 pt-5 pb-8">
      <header className="mb-8 flex items-center gap-3">
        <button type="button" onClick={onBack} className="text-muted text-sm font-bold">
          {es.today.back}
        </button>
        <span className="flex-1" />
        <span className="text-gold text-lg font-extrabold">
          {member.coins}{' '}
          <span className="text-muted text-sm font-bold">{member.coinWord ?? es.today.coins}</span>
        </span>
      </header>

      <p className="text-muted mb-4 text-xs font-bold tracking-[0.12em] uppercase">
        {es.today.onlyOne}
      </p>

      {current ? (
        <button
          type="button"
          onClick={() => {
            onOpenTask(current)
          }}
          className="border-rule bg-surface flex flex-col items-center gap-5 rounded-2xl border-4 p-8 text-center"
          style={{ borderColor: member.accent }}
        >
          <span aria-hidden="true" className="text-7xl">
            {current.emoji ?? '⭐'}
          </span>
          <span className="text-3xl leading-tight font-extrabold">{current.name}</span>
          {current.steps.length > 0 ? (
            <span className="w-full">
              <span className="text-muted mb-2 block text-base">
                {es.today.stepsDone(progress, current.steps.length)}
              </span>
              {progress > 0 ? (
                <Meter done={progress} total={current.steps.length} accent={member.accent} />
              ) : null}
            </span>
          ) : null}
        </button>
      ) : (
        <p className="text-mint py-10 text-center text-2xl font-extrabold">
          {es.today.nothingLeft}
        </p>
      )}

      {routine && after ? (
        <p className="text-muted mt-4 text-center text-sm">{es.today.nextAfter(after.name)}</p>
      ) : null}

      {!routine && missions.length > 1 ? (
        <button
          type="button"
          className="text-muted mt-5 self-center text-sm font-bold underline underline-offset-4"
          onClick={() => {
            setMissionIndex((n) => n + 1)
          }}
        >
          {es.today.anotherMission}
        </button>
      ) : null}

      <section className="border-gold bg-gold-soft mt-10 rounded-xl border-t-4 p-5 text-center">
        <p className="m-0 mb-2 text-lg font-extrabold">
          🎁 {today.chestOpened ? es.today.chestOpened(today.chestMinutes) : es.today.chest}
        </p>
        {today.chestOpened ? (
          <button
            type="button"
            className="bg-teal rounded-lg px-6 py-3 font-bold text-white"
            onClick={onTimer}
          >
            {es.today.timer}
          </button>
        ) : chestBlocked === null ? (
          <button
            type="button"
            className="bg-teal rounded-lg px-6 py-3 font-bold text-white"
            onClick={onOpenChest}
          >
            {es.today.chestOpen}
          </button>
        ) : (
          <p className="m-0 text-sm">{chestBlocked}</p>
        )}
      </section>

      <div className="mt-auto flex justify-center gap-6 pt-10">
        <button type="button" className="text-muted text-sm font-bold" onClick={onShop}>
          🛒 {es.today.shop}
        </button>
        <button type="button" className="text-muted text-sm font-bold" onClick={onJuegos}>
          🎮 {es.today.juegos}
        </button>
        <button type="button" className="text-muted text-sm font-bold" onClick={onSeeAll}>
          {es.today.seeAll}
        </button>
      </div>
    </div>
  )
}
